/** #434 (SPEC-434 3.1): compare two versions of one grid; every changed row, never truncated. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, LoadingSpinner, Pagination, Select } from '@/components/common';
import { commissionGridService, type GridCompareStatus } from '@/services/commissionGrids';
import { getErrorMessage } from '@/services/api';
import { COMPARE_LABELS, COMPARE_ORDER, saveBlob } from './gridPages';

const PAGE_SIZE = 20;
const VARIANT: Record<GridCompareStatus, 'warning' | 'success' | 'error' | 'info'> = {
  changed: 'warning',
  basis_changed: 'warning',
  region_changed: 'info',
  added: 'success',
  removed: 'error',
};

export function GridComparePage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const grid = useQuery({
    queryKey: ['commission-grid', id],
    queryFn: () => commissionGridService.getGrid(id),
  });
  const result = useQuery({
    queryKey: ['commission-grid-compare', from, to, status, page],
    queryFn: () => commissionGridService.compare(from, to, status, page),
    enabled: Boolean(from && to),
  });

  const setVersion = (key: 'from' | 'to', value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setPage(1);
    setParams(next);
  };
  const exportCsv = async () => {
    try {
      saveBlob(await commissionGridService.compareCsv(from, to, status), 'grid-compare.csv');
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const options = (grid.data?.versions ?? []).map((v) => ({
    value: v.id,
    label: `v${v.version_no} (${v.effective_from})`,
  }));
  const data = result.data;
  const total = data?.count ?? 0;

  return (
    <div>
      <PageHeader
        title="Compare versions"
        subtitle={grid.data ? `${grid.data.name} - ${grid.data.insurer_name}` : undefined}
        breadcrumbs={[
          { label: 'Commissions', href: '/commissions' },
          { label: 'Grids', href: '/commissions/grids' },
          { label: grid.data?.name ?? 'Grid', href: `/commissions/grids/${id}` },
          { label: 'Compare' },
        ]}
        actions={
          <Button variant="outline" onClick={() => navigate(`/commissions/grids/${id}`)}>
            Back to versions
          </Button>
        }
      />
      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            id="cmp-from"
            label="From (older)"
            value={from}
            placeholder="Choose"
            options={options}
            onChange={(e) => setVersion('from', e.target.value)}
          />
          <Select
            id="cmp-to"
            label="To (newer)"
            value={to}
            placeholder="Choose"
            options={options}
            onChange={(e) => setVersion('to', e.target.value)}
          />
          <Select
            id="cmp-show"
            label="Show"
            value={status}
            options={[
              { value: '', label: 'All changes' },
              ...COMPARE_ORDER.map((s) => ({ value: s, label: COMPARE_LABELS[s] })),
            ]}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          />
        </div>
      </Card>
      {!from || !to ? (
        <p className="text-sm text-gray-500">Choose two versions to compare.</p>
      ) : null}
      {result.isLoading && from && to && <LoadingSpinner />}
      {result.error && (
        <p role="alert" className="text-sm text-red-600">
          {getErrorMessage(result.error)}
        </p>
      )}
      {data && (
        <Card>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{data.counts.total} changed rows</span>
            {COMPARE_ORDER.map((s) => (
              <Badge key={s} variant={VARIANT[s]}>
                {COMPARE_LABELS[s]}: {data.counts[s]}
              </Badge>
            ))}
            <Button className="ml-auto" size="sm" variant="outline" onClick={exportCsv}>
              Export CSV
            </Button>
          </div>
          {total === 0 ? (
            <p className="text-sm text-gray-500">No rates differ.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4">Change</th>
                    <th className="py-2 pr-4">Class</th>
                    <th className="py-2 pr-4">Segment</th>
                    <th className="py-2 pr-4">Region</th>
                    <th className="py-2 pr-4">Old rate</th>
                    <th className="py-2 pr-4">New rate</th>
                    <th className="py-2 pr-4">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((e, i) => (
                    <tr
                      key={`${e.status}-${e.old?.source_ref ?? ''}-${e.new?.source_ref ?? ''}-${i}`}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <td className="py-2 pr-4">
                        <Badge variant={VARIANT[e.status]}>{COMPARE_LABELS[e.status]}</Badge>
                      </td>
                      <td className="py-2 pr-4">{e.lob}</td>
                      <td className="py-2 pr-4">{e.insurer_segment || e.class_group}</td>
                      <td className="py-2 pr-4">{e.geo_label}</td>
                      <td className="py-2 pr-4">{e.old?.rate ?? '-'}</td>
                      <td
                        data-testid="new-rate"
                        className={`py-2 pr-4 font-medium ${e.status === 'added' || e.status === 'changed' ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}
                      >
                        {e.new?.rate ?? '-'}
                      </td>
                      <td className="py-2 pr-4">{e.difference ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > PAGE_SIZE && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / PAGE_SIZE)}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </Card>
      )}
    </div>
  );
}
